import {
  ForwardRefRenderFunction,
  MouseEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'

import { IUniform, PointCloudMaterial, PointCloudOctree } from '@datalabs-jp/three-loader'
import { InspectionArea } from '@editor/interfaces/inspection'
import CircleAnchor from '@editor/pages/projects/editor/objects/EditorCircleAnchor'
import usePCDLoader from '@editor/pages/projects/hooks/usePCDLoader'
import { focusCamera, getCenterAndRadius } from '@editor/services/Editor'
import { getPerpendicularIntersection, resetPCD } from '@editor/services/Points'
import { withPromiseContained } from '@editor/services/Util'
import { ArcballControls } from '@editor/services/threejs'
import { RootState, useAppDispatch } from '@editor/store/app'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { NormalBlending, Points, PointsMaterial, Vector3 } from 'three'
import { useDebouncedCallback, useThrottledCallback } from 'use-debounce'

import { ANCHOR_COLORS, Mode, SceneEventHandlers, Steps, Tools, ViewSides } from '../definitions'
import { usePointSelection } from '../hooks/usePointSelection'
import { addLeftAnchor, addRightAnchor, clearAnchors } from '../store'
import { MainSceneProps } from './MainScene'

export type SplitSceneForwardRef = SceneEventHandlers

interface SplitSceneProps extends MainSceneProps {
  side: ViewSides
  inspectionArea: InspectionArea | undefined | null
  onProgress: (percent: number | null) => void
}

/**
 * Threshold for close enough distance between two anchors.
 * Value is in meters.
 */
const DISTANCE_PROXIMITY_THRESHOLD = 0.005

const SplitSceneFn: ForwardRefRenderFunction<SplitSceneForwardRef, SplitSceneProps> = (
  { potree, sceneRef, controlsRef, side, camera, inspectionArea, onProgress },
  ref,
) => {
  // States
  const [pointCloud, setPointCloud] = useState<Points | PointCloudOctree>()
  const [material, setMaterial] = useState<PointsMaterial | PointCloudMaterial>()
  const [mouseAnchor, setMouseAnchor] = useState<Vector3 | null>(null)
  const [initCompleted, setInitCompleted] = useState(false)
  const [currentAreaId, setCurrentAreaId] = useState<string | null>(inspectionArea?.inspection_area_id || null)
  const [bboxCenter, setBboxCenter] = useState<Vector3 | null>(null)
  const [radius, setRadius] = useState(0)
  const [toggleControls, setToggleControls] = useState(false)

  // Store
  const dispatch = useAppDispatch()
  const currentMode = useSelector((state: RootState) => state.pcdAlignment.currentMode)
  const currentTool = useSelector((state: RootState) => state.pcdAlignment.currentTool)
  const currentSide = useSelector((state: RootState) => state.pcdAlignment.currentSide)
  const currentStep = useSelector((state: RootState) => state.pcdAlignment.currentStep)
  const leftAnchors = useSelector((state: RootState) => state.pcdAlignment.leftAnchors)
  const rightAnchors = useSelector((state: RootState) => state.pcdAlignment.rightAnchors)
  const canvasRect = useSelector((state: RootState) => state.pcdAlignment.canvasRect)
  const anchors = useMemo(
    () => (side === ViewSides.Left ? leftAnchors : rightAnchors).map((anchor) => new Vector3(...anchor)),
    [leftAnchors, rightAnchors, side],
  )
  const otherAnchors = useMemo(
    () => (side === ViewSides.Left ? rightAnchors : leftAnchors).map((anchor) => new Vector3(...anchor)),
    [leftAnchors, rightAnchors, side],
  )
  const canAddPoints = useMemo(
    () => anchors.length < 3 && currentStep === Steps.SelectPoints && currentMode === Mode.Normal,
    [anchors, currentStep, currentMode],
  )
  const isSelectedTool = useMemo(() => currentTool === Tools.PCDAlignment, [currentTool])
  const isControlsEnabled = useMemo(
    () => currentSide === side && currentStep === Steps.SelectPoints && isSelectedTool,
    [currentSide, side, currentStep, isSelectedTool],
  )

  // Hooks
  const location = useLocation()
  const { loadPCD } = usePCDLoader(potree)
  const { projectPoint } = usePointSelection([pointCloud], camera, () => {
    const { x, y, width, height } = canvasRect
    return {
      x: side === ViewSides.Left ? x : x + width / 2,
      y,
      width: width / 2,
      height,
    }
  })

  /**
   * If we're on the second part of second step, user is restricted to the distance
   * between the first anchor and the new second anchor.
   */
  const anchor2Highlight = useMemo(() => {
    if (currentStep !== Steps.SelectPoints || anchors.length !== 1 || otherAnchors.length < 2) return null

    const distance = new Vector3(...otherAnchors[1]).distanceTo(new Vector3(...otherAnchors[0]))
    return {
      distance,
      max: distance + DISTANCE_PROXIMITY_THRESHOLD,
      min: distance - DISTANCE_PROXIMITY_THRESHOLD,
    }
  }, [currentStep, anchors, otherAnchors])

  /**
   * Calculation of perpendicular intersection for second part of the third anchor.
   */
  const anchor3Highlight = useMemo(() => {
    if (currentStep !== Steps.SelectPoints || anchors.length !== 2 || otherAnchors.length < 3) return null

    // Take the 3rd point and find the perpedicular point on the line between the first and second anchor
    const otherPerpPoint = getPerpendicularIntersection(otherAnchors[0], otherAnchors[1], otherAnchors[2])
    const otherDistance = otherPerpPoint.distanceTo(otherAnchors[2])

    // Find the duration between the first and the perpendicular point
    const AB = new Vector3().subVectors(otherAnchors[1], otherAnchors[0])
    const AC = new Vector3().subVectors(otherPerpPoint, otherAnchors[0])
    const t = AC.dot(AB) / AB.lengthSq()

    // Find the perpendicular point on current side's anchors
    const DE = new Vector3().subVectors(anchors[1], anchors[0])
    const perpPoint = new Vector3().addVectors(anchors[0], DE.multiplyScalar(t))

    return {
      perpPoint,
      distance: {
        distance: otherDistance,
        max: otherDistance + DISTANCE_PROXIMITY_THRESHOLD,
        min: otherDistance - DISTANCE_PROXIMITY_THRESHOLD,
      },
    }
  }, [currentStep, otherAnchors, anchors])

  /**
   * Reset scene when switching inspection area.
   */
  useEffect(() => {
    if (
      currentStep === Steps.SelectPoints &&
      currentAreaId !== inspectionArea?.inspection_area_id &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      ((location.state?.navigated && side === ViewSides.Right) || side === ViewSides.Left)
    ) {
      setInitCompleted(false)
      dispatch(clearAnchors())
    }
  }, [currentAreaId, inspectionArea, currentStep, side, location, dispatch])

  /**
   * Get selected point from the point cloud.
   *
   * @param ev Mouse event
   * @param checkConstraint Whether to check the constraint restriction.
   */
  const getSelectedPoint = useCallback(
    (ev: MouseEvent<HTMLDivElement>) => {
      const intersectedPoint = projectPoint(ev)
      if (!intersectedPoint) return null

      // Check if the distance between the new anchor and the first anchor is within the threshold
      if (anchor2Highlight && anchors.length === 1 && currentStep === Steps.SelectPoints) {
        const distance = new Vector3().copy(intersectedPoint || new Vector3()).distanceTo(anchors[0])
        if (distance < anchor2Highlight.min || distance > anchor2Highlight.max) return null
      } else if (anchor3Highlight && anchors.length === 2 && currentStep === Steps.SelectPoints) {
        const currentPerpIntersection = getPerpendicularIntersection(anchors[0], anchors[1], intersectedPoint)
        const currentDistance = currentPerpIntersection.distanceTo(intersectedPoint)

        if (
          anchor3Highlight.perpPoint.distanceTo(currentPerpIntersection) > DISTANCE_PROXIMITY_THRESHOLD ||
          currentDistance < anchor3Highlight.distance.min ||
          currentDistance > anchor3Highlight.distance.max
        ) {
          return null
        }
      }

      return intersectedPoint
    },
    [anchor2Highlight, anchor3Highlight, anchors, currentStep, projectPoint],
  )

  /**
   * Load PCD file and set camera position.
   */
  const init = useDebouncedCallback(
    (area: InspectionArea) => {
      if (!area.origin_file?.path) {
        onProgress(-1)
        return
      }

      setPointCloud(undefined)
      void withPromiseContained(async () => {
        const pcd = await loadPCD({ inspectionArea: area }, onProgress)
        if (!pcd) return

        // Reset everything first before applying the transformation
        // This is necessary to avoid accumulating transformations
        resetPCD(pcd)

        if (pcd.material instanceof PointsMaterial || pcd.material instanceof PointCloudMaterial) {
          setMaterial(pcd.material.clone())
        }

        // get bounding box center and radius
        const { radius: rds, center } = getCenterAndRadius(pcd)

        setBboxCenter(center)
        setRadius(rds)
        setPointCloud(pcd)
        setCurrentAreaId(area.inspection_area_id)
      }).finally(() => {
        onProgress(null)
        setInitCompleted(true)
      })
    },
    2000,
    { leading: true, trailing: true },
  )

  /**
   * Load PCD file when entering split view.
   */
  useEffect(() => {
    if (!inspectionArea || initCompleted || currentTool !== Tools.PCDAlignment || currentStep === Steps.FineTuneOrSave)
      return

    init(inspectionArea)
  }, [inspectionArea, initCompleted, currentTool, currentStep, init, loadPCD, onProgress])

  const reset = useCallback(() => {
    // reset material
    if (pointCloud && material) {
      pointCloud.material = material.clone()
      material.dispose()
    }

    setBboxCenter(null)
    setRadius(0)
    setInitCompleted(false)
    setPointCloud(undefined)
    setMaterial(undefined)
    setMouseAnchor(null)
  }, [pointCloud, material])

  /**
   * Handle scene cleanup.
   */
  useEffect(() => {
    if (!inspectionArea) {
      // If inspection area was reset but point cloud is still there, we need to reset the point cloud
      if (pointCloud) reset()
      return
    }

    if (currentStep !== Steps.SelectPoints || currentTool !== Tools.PCDAlignment) {
      reset()
    }
  }, [currentStep, currentTool, inspectionArea, pointCloud, reset])

  useEffect(() => {
    if (leftAnchors.length === 0 && rightAnchors.length === 0) {
      setMouseAnchor(null)
    }
  }, [leftAnchors, rightAnchors])

  /**
   * Modify shaders to highlight possible clickable points.
   */
  useEffect(() => {
    if (!pointCloud || !material) return

    // Potree works differently. Shaders are already set up in the material, just need to update uniforms
    // albeit differently than the PCDLoader material
    if (pointCloud.material instanceof PointCloudMaterial) {
      if (currentStep === Steps.SelectPoints && anchor2Highlight) {
        pointCloud.material.highlightedType = 2
        pointCloud.material.highlightedPoint0 = new Vector3(...anchors[0])
        pointCloud.material.highlightedMinDistance = anchor2Highlight.min
        pointCloud.material.highlightedMaxDistance = anchor2Highlight.max
        pointCloud.material.enableTransparency()
        pointCloud.material.blending = NormalBlending
      } else if (currentStep === Steps.SelectPoints && anchor3Highlight) {
        pointCloud.material.highlightedType = 3
        pointCloud.material.highlightedPoint0 = new Vector3(...anchors[0])
        pointCloud.material.highlightedPoint1 = new Vector3(...anchors[1])
        pointCloud.material.highlightedPoint2 = anchor3Highlight.perpPoint
        pointCloud.material.highlightedMinDistance = anchor3Highlight.distance.min
        pointCloud.material.highlightedMaxDistance = anchor3Highlight.distance.max
        pointCloud.material.enableTransparency()
        pointCloud.material.blending = NormalBlending
      } else {
        pointCloud.material.disableTransparency()
        pointCloud.material.highlightedType = 0
      }

      pointCloud.material.highlightedDistanceProximityThreshold = DISTANCE_PROXIMITY_THRESHOLD
      pointCloud.material.highlightedOuterOpacity = 0.2
      pointCloud.material.needsUpdate = true

      pointCloud.material.updateShaders()

      return
    }

    // For Step2, pass the second anchor and its distance to the first anchor
    // For Step3, pass the perpendicular point and its distance to the third anchor
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const uniforms: Record<string, IUniform<any>> = {
      distanceProximityThreshold: { type: 'f', value: DISTANCE_PROXIMITY_THRESHOLD },
      step: { type: 'i', value: 0 },
    }

    if (currentStep === Steps.SelectPoints && anchor2Highlight) {
      uniforms.step.value = 2
      uniforms.anchor0Position = { type: 'fv', value: new Vector3(...anchors[0]) }
      uniforms.minDistance = { type: 'f', value: anchor2Highlight.min }
      uniforms.maxDistance = { type: 'f', value: anchor2Highlight.max }
    } else if (currentStep === Steps.SelectPoints && anchor3Highlight) {
      uniforms.step.value = 3
      uniforms.anchor0Position = { type: 'fv', value: new Vector3(...anchors[0]) }
      uniforms.anchor1Position = { type: 'fv', value: new Vector3(...anchors[1]) }
      uniforms.targetPerpPosition = { type: 'fv', value: anchor3Highlight.perpPoint }
      uniforms.minDistance = { type: 'f', value: anchor3Highlight.distance.min }
      uniforms.maxDistance = { type: 'f', value: anchor3Highlight.distance.max }
    }

    // PCDLoader material
    const updatedMat = material.clone()

    updatedMat.onBeforeCompile = (shader) => {
      shader.uniforms = { ...(shader.uniforms || {}), ...uniforms }
      shader.vertexShader = shader.vertexShader.replace(
        `void main() {`,
        `
            varying float vIsHighlighted;

            uniform vec3 anchor0Position;
            uniform vec3 anchor1Position;
            uniform vec3 targetPerpPosition;
            uniform float minDistance;
            uniform float maxDistance;
            uniform float distanceProximityThreshold;
            uniform int step;

            /**
             * Computes the perpendicular intersection of a point onto a line defined by two points.
             * @param p1 - First point of the line.
             * @param p2 - Second point of the line.
             * @param p3 - Point to project onto the line.
             * @returns The intersection point.
             */
            vec3 getPerpendicularIntersection(vec3 p1, vec3 p2, vec3 p3) {
                vec3 lineDir = normalize(p2 - p1); // Line direction unit vector
                vec3 p1ToP3 = p3 - p1; // Vector from P1 to P3

                float projectionLength = dot(p1ToP3, lineDir); // Projection scalar
                vec3 intersection = p1 + lineDir * projectionLength; // Compute intersection

                return intersection;
            }

            void main() {
              vec4 vFragPosition = modelMatrix * vec4(position, 1.0);
              vec3 vAnchor0Position = anchor0Position;
              float vMinDistance = minDistance;
              float vMaxDistance = maxDistance;

              vIsHighlighted = 0.0;

              if (step == 2) {
                float distance = distance(vFragPosition.xyz, anchor0Position);
              
                // anything within min/max distance will be highlighted
                if (distance > vMinDistance && distance < vMaxDistance) {
                  vIsHighlighted = 1.0;
                }
              } else if (step == 3) {
                vec3 currPerpPosition = getPerpendicularIntersection(anchor0Position, anchor1Position, vFragPosition.xyz);
                float perpDistance = distance(targetPerpPosition, currPerpPosition);  // Distance between calculated perpendicular and target perpendicular
                float currDistance = distance(currPerpPosition, vFragPosition.xyz);   // Distance between current position and calculated perpendicular

                if (currDistance >= minDistance  && currDistance <= maxDistance  && perpDistance <= distanceProximityThreshold) {
                  vIsHighlighted = 1.0;
                }
              }
            `,
      )
      shader.fragmentShader = shader.fragmentShader
        .replace(
          `void main() {`,
          `
          varying float vIsHighlighted;
          uniform int step;

          vec4 addTint(vec4 originalColor, vec3 tintColor, float intensity) {
            return vec4(mix(originalColor.rgb, tintColor, intensity), originalColor.a);
          }

          void main() {
          `,
        )
        .replace(
          /}(?![\s\S]*})/g,
          `
            if (step == 2 || step == 3) {
              if (vIsHighlighted == 1.0) {
                gl_FragColor = addTint(diffuseColor, vec3(0.75, 1.0, 0.0), 0.7);
              } else {
               gl_FragColor = vec4(diffuseColor.rgb, 0.2);
              }
            }
          }
          `,
        )
    }

    updatedMat.needsUpdate = true
    pointCloud.material = updatedMat
  }, [pointCloud, material, anchors, anchor2Highlight, anchor3Highlight, currentStep])

  /**
   * Throttle mouse anchor update to avoid performance issues.
   */
  const updateMouseAnchor = useThrottledCallback((ev: MouseEvent<HTMLDivElement>) => {
    if (!pointCloud || !material) return

    const intersectedPoint = getSelectedPoint(ev)
    if (intersectedPoint) {
      setMouseAnchor(intersectedPoint)
    } else {
      setMouseAnchor(null)
    }
  }, 1000 / 30)

  /**
   * Handle user interaction event.
   */
  useImperativeHandle(
    ref,
    () => ({
      onClick: (ev) => {
        if (!canAddPoints) return

        const intersectedPoint = getSelectedPoint(ev)
        if (intersectedPoint) {
          if (side === ViewSides.Left) {
            dispatch(addLeftAnchor(intersectedPoint.toArray()))
          } else {
            dispatch(addRightAnchor(intersectedPoint.toArray()))
          }
          setMouseAnchor(null)
        }
      },
      onMove: (ev) => {
        if (!canAddPoints) return

        updateMouseAnchor(ev)
      },
      onFocus: (ev) => {
        const point = projectPoint(ev)
        if (!controlsRef.current || !point) return
        controlsRef.current.focus(point, 1)
      },
    }),
    [controlsRef, canAddPoints, side, updateMouseAnchor, dispatch, getSelectedPoint, projectPoint],
  )

  useEffect(() => {
    const canvasBox: HTMLCanvasElement | null = document.querySelector('#canvasBox canvas')
    if (!sceneRef.current || !canvasBox || !bboxCenter || !pointCloud || !inspectionArea || controlsRef.current)
      return () => null

    controlsRef.current = new ArcballControls(camera, canvasBox, sceneRef.current)
    controlsRef.current.enableFocus = false
    controlsRef.current.enableAnimations = false
    controlsRef.current.activateGizmos(false)
    controlsRef.current.setGizmosVisible(false)

    if (inspectionArea.camera_profile) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
      ;(controlsRef.current as any).setStateFromJSON(JSON.stringify(inspectionArea.camera_profile.state))
    } else {
      const cameraPos = bboxCenter.clone().setZ(bboxCenter.z + radius)
      camera.position.copy(cameraPos)
      focusCamera(controlsRef.current, camera, bboxCenter, Infinity)
    }

    setToggleControls(true)

    return () => {
      controlsRef.current?.dispose()
      controlsRef.current = null
      setToggleControls(false)
    }
  }, [controlsRef, camera, inspectionArea, pointCloud, bboxCenter, radius, sceneRef])

  useEffect(() => {
    if (!controlsRef.current) return
    controlsRef.current.enabled = isControlsEnabled
  }, [isControlsEnabled, toggleControls, inspectionArea, controlsRef])

  return (
    <scene ref={sceneRef}>
      {pointCloud ? <primitive object={pointCloud} /> : null}

      {anchors.map((anchor, idx) => (
        <CircleAnchor
          id={`anchor-${side}-${idx}`}
          key={anchor.toArray().join()}
          point={anchor.toArray()}
          color={ANCHOR_COLORS[idx]}
          customCamera={camera}
        />
      ))}

      {/* visible (not opacity 0): opacity only fades the fill — the outline ring would
          stay as a ghost dot at the placeholder [0,0,0] once all 3 anchors are placed. */}
      <CircleAnchor
        id={`mouse-anchor-${side}`}
        visible={Boolean(currentSide === side && mouseAnchor && canAddPoints)}
        point={mouseAnchor ? mouseAnchor.toArray() : [0, 0, 0]}
        color={ANCHOR_COLORS[anchors.length]}
        customCamera={camera}
      />

      <ambientLight intensity={Math.PI} />
    </scene>
  )
}

const SplitScene = forwardRef(SplitSceneFn)
export default SplitScene
